import React, { useEffect, useMemo, useState } from 'react'
import { DotLottieReact } from '@lottiefiles/dotlottie-react'
import { Box, LinearProgress, Typography } from '@mui/material'

import { TownHallIcon } from '~assets/icons'
import { darkTheme, lightTheme } from '~createTheme'
import { EnrichedOrganizationAISettings } from '~types'

export type ChatBubbleLoadingState = 'analyzing' | 'retrieving' | 'generating' | 'waiting'

interface ChatBubbleProgressIndicatorProps {
  loadingState: ChatBubbleLoadingState
  showBubble?: boolean
  backgroundColor?: string
  onComplete?: boolean
  progressWidth?: number | string
  completionDuration?: number
  orgAiSettings?: EnrichedOrganizationAISettings
}

const ChatBubbleProgressIndicator = React.forwardRef<
  HTMLDivElement,
  ChatBubbleProgressIndicatorProps
>(
  (
    {
      loadingState,
      showBubble = true,
      backgroundColor = 'white',
      onComplete = false,
      progressWidth = 240,
      completionDuration = 80,
      orgAiSettings,
    },
    ref,
  ) => {
    const internalRef = React.useRef<HTMLDivElement>(null)
    const resolvedRef = ref || internalRef
    const searchParams = new URLSearchParams(window?.location?.search || '')
    const themeMode = searchParams.get('theme')
    const theme = themeMode === 'dark' ? darkTheme : lightTheme
    const [progress, setProgress] = useState(0)
    const [isCompleting, setIsCompleting] = useState(false)

    const stateMessages = {
      analyzing: 'Analyzing your question',
      retrieving: 'Searching organization',
      generating: 'Generating response',
      waiting: 'This may take a moment',
    }

    // Map loading states to progress ranges
    const progressRanges = {
      analyzing: [0, 25],
      retrieving: [25, 50],
      generating: [50, 80],
      waiting: [80, 95],
    }

    useEffect(() => {
      // When onComplete becomes true, trigger the completion animation
      if (onComplete && !isCompleting) {
        setIsCompleting(true)
      }
    }, [onComplete])

    useEffect(() => {
      const focusTimer = setTimeout(() => {
        if (resolvedRef && typeof resolvedRef !== 'function') {
          resolvedRef.current?.focus()
        }
      }, 100)

      return (): void => clearTimeout(focusTimer)
    }, [])

    useEffect(() => {
      let timer: NodeJS.Timeout

      if (isCompleting) {
        // Animate to 100% completion
        const remaining = 100 - progress
        const steps = Math.max(5, Math.ceil(remaining / 2))
        const stepSize = remaining / steps
        const interval = completionDuration / steps

        timer = setInterval(() => {
          setProgress((prev) => {
            const nextProgress = prev + stepSize
            if (nextProgress >= 100) {
              clearInterval(timer)
              return 100
            }
            return nextProgress
          })
        }, interval)
      } else {
        // Normal progress animation based on loading state
        const targetProgress = progressRanges[loadingState][1]

        timer = setInterval(() => {
          setProgress((prev) => {
            // Only increment if we haven't reached the target for current state
            if (prev < targetProgress) {
              return Math.min(prev + 0.5, targetProgress)
            }
            return prev
          })
        }, 30)
      }

      return (): void => clearInterval(timer)
    }, [loadingState, isCompleting, completionDuration, progress])

    const progressContent = (
      <>
        {/* Town Hall icon with Lottie magnifying glass overlay */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 1,
            width: 'fit-content',
            alignSelf: 'center',
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <TownHallIcon width={40} height={40} />
          </Box>
          <Box
            sx={{
              position: 'absolute',
              top: -4,
              left: -4,
              right: -4,
              bottom: -4,
              zIndex: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {useMemo(
              () => (
                <DotLottieReact
                  src='https://lottie.host/8b9a70ba-9608-4e71-b8da-a3552841a82d/c7RJmpMaB4.lottie'
                  loop
                  autoplay
                  style={{ width: '110%', height: '110%' }}
                />
              ),
              [],
            )}{' '}
          </Box>
        </Box>

        <Typography
          variant='body1'
          fontFamily={orgAiSettings?.font_family || 'inherit'}
          color={theme.palette.text.primary}
          sx={{ marginBottom: 1, alignSelf: 'center' }}
        >
          {stateMessages[loadingState]}
        </Typography>

        <Box sx={{ width: progressWidth, alignSelf: 'center' }}>
          <LinearProgress
            variant='determinate'
            value={progress}
            sx={{
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.grey[200],
              '& .MuiLinearProgress-bar': {
                borderRadius: 2,
                backgroundColor: theme.palette.primary.main,
                transition: 'transform 0.1s linear',
              },
            }}
          />
        </Box>
      </>
    )

    if (!showBubble) {
      return (
        <Box
          ref={resolvedRef}
          role='status'
          aria-label={stateMessages[loadingState]}
          aria-live='polite'
          tabIndex={0}
          sx={{
            display: 'inline-flex',
            flexDirection: 'column',
            marginBottom: 2,
            alignItems: 'center',
          }}
        >
          {progressContent}
        </Box>
      )
    }

    return (
      <Box
        ref={resolvedRef}
        role='status'
        aria-label={stateMessages[loadingState]}
        tabIndex={0}
        sx={{
          display: 'inline-flex',
          flexDirection: 'column',
          backgroundColor: backgroundColor,
          color: 'text.primary',
          borderRadius: 2,
          padding: 2,
          maxWidth: '70%',
          boxShadow: 1,
          marginTop: 2,
          alignItems: 'center',
        }}
      >
        {progressContent}
      </Box>
    )
  },
)

export default ChatBubbleProgressIndicator
